import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Alert, Button, Checkbox, DatePicker, Input, Modal, Radio, Select, Table, Tag, Upload, message} from 'antd'
import moment from 'moment'
import {
    downloadWorkerAccountsTemplate,
    getWorkerAccounts,
    importWorkerAccounts,
    previewWorkerAccountsImport,
} from '../../../../services/api/apiProductionPlanning'
import useUrlFilters from '../../../helpers/useUrlFilters'
import {ROUTES} from '../../constants'
import {
    accountImportBlockerOf,
    accountPreviewRowsOf,
    accountsClosingByFileOf,
    accountsToCloseOf,
    accountsWithoutEffectiveFromOf,
    changeAppliedFromOf,
    effectiveAppliedFromOf,
    ambiguousAfterImport,
    ambiguousCodeSet,
    changeCountOf,
    changeKey,
    departmentsOf,
    effectiveFromOf,
    overridePayloadOf,
    searchAccountRows,
} from '../../helpers/workerAccounts'
import {errorMessage} from '../../helpers/apiError'
import {canImportStandardConfig} from '../../helpers/permission'
import {DASH, formatInt} from '../../helpers/format'
import {downloadBlob} from '../../helpers/download'
import LoadError from '../shared/LoadError'
import Notice from '../shared/Notice'

const DAY = 'YYYY-MM-DD'

/**
 * Bộ so sánh một cột chữ.
 *
 * So theo `'vi'` chứ không `localeCompare` mặc định: họ tên có dấu xếp sai bảng chữ cái tiếng Việt
 * thì bảng sắp rồi vẫn khó dò bằng mắt.
 *
 * ⚠️ Không cột nào được sort SẴN. Thứ tự ban đầu của bản xem trước là thứ tự dòng trong file —
 *    thứ duy nhất đối chiếu được với Excel đang mở bên cạnh; sort sẵn là lấy mất mốc đó ngay từ
 *    lúc mở.
 */
const byText = (field) => (a, b) => String(a[field] || '').localeCompare(String(b[field] || ''), 'vi')

/**
 * SCR Tài khoản nhân viên (FAC-26417 FR-001 bước 8) — bản đồ **mã nhân viên ↔ tài khoản hệ thống**.
 *
 * Màn này tồn tại vì file chấm công CHỈ có mã nhân viên còn sản lượng CHỈ có tài khoản. Không có
 * bản đồ thì hai nguồn không nối được với nhau và mẫu số của hiệu suất không nhập được từ file.
 *
 * ⚠️ Bản đồ có **khoảng hiệu lực**, và đó không phải để cho đủ: việc nhập chấm công vốn là nhập bù
 *    (file tháng 7 nhập vào tháng 10). Quy đổi bằng bản đồ của hôm nay là gán giờ của người đã đổi
 *    công đoạn cho tài khoản họ không còn dùng — không lỗi nào báo, con số vẫn trông bình thường.
 *    Vì thế màn này cho xem bản đồ TẠI MỘT NGÀY trong quá khứ, không chỉ bản hiện hành.
 *
 * ⚠️ Chỉ đọc sheet `3D` của file. Sheet `Chat nội bộ` của cùng workbook nói khác về cùng những người
 *    ấy — đo 27/10/2026, nó tráo tài khoản của `00503` và `00507` cho nhau.
 */
function WorkerAccountsPage({history, location}) {
    const defaults = useMemo(() => ({at: '', includeHistory: false, q: '', department: ''}), [])
    const [filters, setFilter] = useUrlFilters(defaults, {history, location})

    const [data, setData] = useState({rows: [], total: 0, liveCount: 0, ambiguous: []})
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(null)

    const [importOpen, setImportOpen] = useState(false)
    const [file, setFile] = useState(null)
    const [preview, setPreview] = useState(null)
    const [previewing, setPreviewing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [downloadingTemplate, setDownloadingTemplate] = useState(false)
    const [previewError, setPreviewError] = useState(null)
    const [overrides, setOverrides] = useState({})
    const [closeMissing, setCloseMissing] = useState(false)
    const [confirmClose, setConfirmClose] = useState(false)
    /** Mốc hiệu lực người dùng CHỌN cho dòng không tự khai. Rỗng = để service dùng mốc mặc định. */
    const [appliedFrom, setAppliedFrom] = useState('')
    /*
     * ⚠️ Mốc ĐÓNG tài khoản vắng mặt là state RIÊNG, không dùng chung với `appliedFrom`.
     *
     *    Hai ô trên cùng màn này từng gọi cùng một `setAppliedFrom`, nên chỉnh ô nào cũng đổi ô kia.
     *    Và câu hướng dẫn ngay trên lại khuyên lùi mốc về quá khứ ("chấm công kỳ trước mốc sẽ không
     *    quy được"), nên người dùng lùi về 15/07 cho dòng mới và vô tình đóng mọi tài khoản vắng
     *    mặt tại 15/07 — sớm hơn chính `effectiveFrom` của chúng. Service nay chặn bằng
     *    `assertAfter`, nhưng để hai ô dính nhau thì người dùng chỉ nhận một lỗi 400 khó hiểu cho
     *    một việc họ không định làm. (Vòng review ngoài 03/11/2026, F5.)
     */
    const [closeAt, setCloseAt] = useState('')
    const [previewMode, setPreviewMode] = useState('all')
    const [previewSearch, setPreviewSearch] = useState('')
    const [previewDepartment, setPreviewDepartment] = useState('')

    const canImport = canImportStandardConfig()

    const fetchRows = useCallback(async () => {
        setLoading(true)
        setLoadError(null)

        try {
            setData(await getWorkerAccounts({
                at: filters.at || undefined,
                includeHistory: filters.includeHistory || undefined,
            }))
        } catch (e) {
            setLoadError(e)
        } finally {
            setLoading(false)
        }
    }, [filters.at, filters.includeHistory])

    useEffect(() => {
        fetchRows()
    }, [fetchRows])

    const resetImport = useCallback(() => {
        setFile(null)
        setPreview(null)
        setPreviewError(null)
        setOverrides({})
        setCloseMissing(false)
        setConfirmClose(false)
        setAppliedFrom('')
        setCloseAt('')
        setPreviewMode('all')
        setPreviewSearch('')
        setPreviewDepartment('')
    }, [])

    const onPick = useCallback(async (picked) => {
        setFile(picked)
        setPreview(null)
        setPreviewError(null)
        setOverrides({})
        setConfirmClose(false)
        setAppliedFrom('')
        setCloseAt('')
        setPreviewMode('all')
        setPreviewSearch('')
        setPreviewDepartment('')
        setPreviewing(true)

        try {
            setPreview(await previewWorkerAccountsImport(picked))
        } catch (e) {
            setPreviewError(errorMessage(e) || 'Không đọc được file danh sách tài khoản')
        } finally {
            setPreviewing(false)
        }
    }, [])

    /*
     * Mốc sẽ dùng cho dòng không tự khai ngày. Đổi ô chọn ngày là bản xem trước tính lại NGAY —
     * câu cảnh báo và cột "Áp dụng từ" của bảng cùng đọc giá trị này, nên không có cách nào để màn
     * hình hứa một ngày mà payload gửi đi một ngày khác.
     *
     * Không gọi lại service: bản xem trước của service là hàm thuần của FILE, không phụ thuộc mốc —
     * tải lại 62 dòng chỉ để nhận đúng câu trả lời cũ là bắt người dùng chờ cho vui.
     */
    const appliedFromUsed = useMemo(() => effectiveAppliedFromOf(preview, appliedFrom), [preview, appliedFrom])
    /*
     * ⚠️ Dòng ĐỔI tài khoản dùng mốc KHÁC dòng thêm mới — xem `changeAppliedFromOf`. Dùng chung
     *    `appliedFromUsed` thì cột "Áp dụng từ" hiện 01/08 trong khi DB ghi hôm nay.
     */
    const changeAppliedFrom = useMemo(() => changeAppliedFromOf(preview, appliedFrom), [preview, appliedFrom])

    const onDownloadTemplate = useCallback(async () => {
        setDownloadingTemplate(true)

        try {
            downloadBlob(await downloadWorkerAccountsTemplate(), 'mau-danh-sach-tai-khoan.xlsx')
        } catch (e) {
            message.error(errorMessage(e) || 'Không tải được file mẫu')
        } finally {
            setDownloadingTemplate(false)
        }
    }, [])

    const onSubmit = useCallback(async () => {
        setSaving(true)

        try {
            const result = await importWorkerAccounts(file, {
                /*
                 * ⚠️ Chỉ gửi khi người dùng THẬT SỰ chọn. Bản trước luôn gửi `defaultAppliedFrom`
                 *    (hôm nay), nên mốc mặc định của service không bao giờ có cơ hội chạy và mọi
                 *    dòng mới chỉ có hiệu lực từ hôm nay — chấm công kỳ trước mất tài khoản.
                 */
                appliedFrom: appliedFrom || undefined,
                overrides: overridePayloadOf(preview.changed, overrides),
                closeMissing: closeMissing || undefined,
                // Bỏ trống = service đóng tại HÔM NAY. Không mượn `appliedFrom` — xem state ở trên.
                closeAt: closeAt || undefined,
                confirmClose: confirmClose || undefined,
            })

            message.success(
                `Đã nhập: thêm ${formatInt(result.added)} · đổi ${formatInt(result.changed)} · ` +
                    `cập nhật ${formatInt(result.touched)}` +
                    (result.closed > 0 ? ` · đóng ${formatInt(result.closed)}` : '')
            )
            resetImport()
            setImportOpen(false)
            await fetchRows()
        } catch (e) {
            message.error(errorMessage(e) || 'Không nhập được. Vui lòng thử lại')
        } finally {
            setSaving(false)
        }
    }, [file, preview, overrides, closeMissing, closeAt, confirmClose, appliedFrom, fetchRows, resetImport])

    // Lọc danh sách chính ở CLIENT, không gọi lại service: bảng này cỡ chục đến trăm dòng và đã
    // nằm sẵn trong bộ nhớ — gọi lại là thêm một vòng chờ cho mỗi ký tự gõ vào.
    const visibleRows = useMemo(
        () => searchAccountRows(data.rows, filters.q, filters.department),
        [data.rows, filters.q, filters.department]
    )
    const departments = useMemo(() => departmentsOf(data.rows), [data.rows])

    const blocker = accountImportBlockerOf(preview, confirmClose)
    const toClose = useMemo(() => accountsToCloseOf(preview), [preview])
    const closingByFile = useMemo(() => accountsClosingByFileOf(preview), [preview])
    const withoutFrom = useMemo(() => accountsWithoutEffectiveFromOf(preview), [preview])
    const changeCount = changeCountOf(preview)
    const ambiguous = useMemo(() => ambiguousAfterImport(preview), [preview])
    const ambiguousCodes = useMemo(() => ambiguousCodeSet(preview), [preview])
    const previewScope = useMemo(() => accountPreviewRowsOf(preview, previewMode), [preview, previewMode])
    const previewRows = useMemo(
        () => searchAccountRows(previewScope, previewSearch, previewDepartment),
        [previewScope, previewSearch, previewDepartment]
    )
    const ambiguousRowCount = useMemo(() => accountPreviewRowsOf(preview, 'ambiguous').length, [preview])
    const previewDepartments = useMemo(
        // Danh sách bộ phận lấy từ TOÀN BỘ file, không từ tập đang lọc: lấy từ tập đã lọc thì chọn
        // một bộ phận xong các lựa chọn khác biến mất và không quay lại được.
        () => departmentsOf(accountPreviewRowsOf(preview, 'all')),
        [preview]
    )

    /** Bảng 62 dòng sẽ ghi — con số tóm tắt không nói được tài khoản nào gắn với ai. */
    const previewColumns = [
        {title: 'Dòng', dataIndex: 'row', width: 64, align: 'right', sorter: (a, b) => a.row - b.row},
        {title: 'Mã NV', dataIndex: 'employeeCode', width: 84, sorter: byText('employeeCode')},
        {
            title: 'Họ tên',
            dataIndex: 'fullName',
            width: 180,
            sorter: byText('fullName'),
            render: (value) => value || DASH,
        },
        {
            title: 'Bộ phận',
            dataIndex: 'department',
            width: 120,
            sorter: byText('department'),
            render: (value) => value || DASH,
        },
        {
            title: 'Tài khoản',
            dataIndex: 'actor',
            sorter: byText('actor'),
            render: (actor, row) => (
                // Dòng đổi tài khoản hiện CẢ tài khoản cũ: chỉ hiện cái mới thì người soát không
                // biết mình vừa thay cái gì, và tài khoản cũ không lấy lại được sau khi ghi.
                (row.previousActors || []).length > 0
                    ? (
                        <span className="VcOverwrite">
                            <span className="VcOverwrite__from">{row.previousActors.join(' · ')}</span>
                            {' → '}
                            <strong>{actor}</strong>
                        </span>
                    )
                    : <span className="VcHoursActor">{actor}</span>
            ),
        },
        {
            title: 'Ghi chú',
            key: 'note',
            width: 240,
            render: (text, row) => {
                // Màu không phải kênh duy nhất (AC-UI-01) — lý do luôn là CHỮ.
                if (ambiguousCodes[row.employeeCode]) {
                    return <span className="VcWarnReason">Mã này sẽ có 2 tài khoản — chấm công bị bỏ qua</span>
                }

                /*
                 * ⚠️ Đọc NHÃN NHÓM (`changeKind`) chứ không suy từ `previousActors`. Dòng sửa mốc
                 *    không có `previousActors` nên lối suy cũ xếp nó vào "Thêm mới" — một thẻ XANH
                 *    vô hại cho việc duy nhất trong lượt nhập làm đổi cách quy đổi dữ liệu đã có:
                 *    thu hẹp khoảng hiệu lực là đẩy chấm công TRƯỚC mốc mới sang `no_account`.
                 *    (Vòng review 2, R1.)
                 */
                if (row.changeKind === 'retimed') {
                    return (
                        <span className="VcOverwrite">
                            <Tag color="orange">Sửa mốc</Tag>
                            <span className="VcOverwrite__from">{row.previousFrom || 'từ trước tới nay'}</span>
                            {' → '}
                            <strong>{row.effectiveFrom}</strong>
                        </span>
                    )
                }

                return (row.previousActors || []).length > 0
                    ? <Tag color="blue">Đổi tài khoản</Tag>
                    : <Tag color="green">Thêm mới</Tag>
            },
        },
    ]

    const columns = [
        {title: 'Mã NV', dataIndex: 'employeeCode', width: 90, sorter: byText('employeeCode')},
        {
            title: 'Họ tên',
            dataIndex: 'fullName',
            width: 190,
            sorter: byText('fullName'),
            render: (value) => value || DASH,
        },
        {
            title: 'Bộ phận',
            dataIndex: 'department',
            width: 130,
            sorter: byText('department'),
            render: (value) => value || DASH,
        },
        {
            title: 'Tài khoản',
            dataIndex: 'actor',
            width: 190,
            sorter: byText('actor'),
            render: (value) => <span className="VcHoursActor">{value}</span>,
        },
        {
            title: 'Hiệu lực',
            key: 'effective',
            width: 210,
            // Sort theo mốc BẮT ĐẦU; dòng "từ trước tới nay" (`null`) luôn đứng đầu vì nó đúng là
            // bản cũ nhất.
            sorter: (a, b) => String(a.effectiveFrom || '').localeCompare(String(b.effectiveFrom || '')),
            render: (text, row) => (
                <span>
                    {/*
                      * `null` hiện "từ trước tới nay", KHÔNG hiện một ngày mốc giả. Dòng của lượt
                      * nhập đầu tiên đúng là có hiệu lực với mọi mốc thời gian, kể cả file chấm công
                      * của ba tháng trước — in ra 01/01/1970 là dạy một ràng buộc không có thật.
                      */}
                    {row.effectiveFrom || 'từ trước tới nay'}
                    {' → '}
                    {row.effectiveTo || 'nay'}
                </span>
            ),
        },
        {
            title: 'Trạng thái',
            key: 'status',
            width: 150,
            sorter: byText('status'),
            render: (text, row) => {
                if (row.effectiveNow && row.status === 'active') return <Tag color="green">Đang dùng</Tag>

                // Chữ đi kèm màu (AC-UI-01). "Đã thay" khác "ngoài khoảng": một cái là bị lượt nhập
                // sau thay thế, cái kia là khoảng hiệu lực không chứa ngày đang xem.
                return row.status === 'expired'
                    ? <Tag>Đã thay</Tag>
                    : <Tag color="orange">Ngoài khoảng đang xem</Tag>
            },
        },
    ]

    const changeColumns = [
        {title: 'Mã NV', dataIndex: 'employeeCode', width: 90},
        {title: 'Họ tên', dataIndex: 'fullName', width: 170},
        {
            title: 'Tài khoản cũ → mới',
            key: 'actor',
            render: (text, row) => (
                <span className="VcOverwrite">
                    <span className="VcOverwrite__from">{(row.previousActors || []).join(' · ') || DASH}</span>
                    {' → '}
                    <strong>{row.actor}</strong>
                </span>
            ),
        },
        {
            title: 'Áp dụng từ',
            key: 'from',
            width: 170,
            render: (text, row) => {
                const key = changeKey(row.employeeCode, row.actor)
                const value = effectiveFromOf(row, overrides, changeAppliedFrom)

                return (
                    <DatePicker
                        size="small"
                        format="DD/MM/YYYY"
                        allowClear={false}
                        value={value ? moment(value, DAY) : null}
                        onChange={(picked) => setOverrides({
                            ...overrides,
                            [key]: picked ? picked.format(DAY) : null,
                        })}
                    />
                )
            },
        },
    ]

    return (
        <div className="VcPage VcWorkerAccountsPage container-fluid">
            <div className="VcPageHead">
                <div>
                    <h1 className="VcPageTitle">Tài khoản nhân viên</h1>
                    <div className="VcPageSubtitle">
                        Bản đồ mã nhân viên ↔ tài khoản hệ thống. File chấm công chỉ có mã nhân viên,
                        sản lượng chỉ có tài khoản — bảng này là cầu nối giữa hai nguồn.
                    </div>
                </div>
                <div className="VcPageActions">
                    {canImport && (
                        <Button type="primary" onClick={() => setImportOpen(true)}>
                            Nhập từ file danh sách
                        </Button>
                    )}
                    <Button onClick={() => history.push(ROUTES.ACTUAL_HOURS)}>Giờ làm thực tế →</Button>
                </div>
            </div>

            <div className="VcFilterBar">
                <div className="VcField">
                    <label>Xem bản đồ tại ngày</label>
                    <DatePicker
                        format="DD/MM/YYYY"
                        value={filters.at ? moment(filters.at, DAY) : null}
                        placeholder="Hôm nay"
                        onChange={(picked) => setFilter({at: picked ? picked.format(DAY) : ''})}
                    />
                    <div className="VcHint">
                        Để trống = bản đang dùng. Chọn một ngày trong quá khứ để biết file chấm công
                        của kỳ đó sẽ quy về tài khoản nào.
                    </div>
                </div>

                <div className="VcField">
                    <label>Bộ phận</label>
                    <Select
                        style={{minWidth: 160}}
                        value={filters.department || undefined}
                        placeholder="Mọi bộ phận"
                        allowClear
                        onChange={(value) => setFilter({department: value || ''})}
                    >
                        {departments.map((name) => (
                            <Select.Option key={name} value={name}>{name}</Select.Option>
                        ))}
                    </Select>
                </div>

                <div className="VcField">
                    <label>Tìm kiếm</label>
                    <Input.Search
                        style={{minWidth: 260}}
                        allowClear
                        value={filters.q}
                        placeholder="Mã NV, họ tên, tài khoản…"
                        onChange={(e) => setFilter({q: e.target.value})}
                    />
                </div>

                <div className="VcField">
                    <label>&nbsp;</label>
                    <Checkbox
                        checked={filters.includeHistory}
                        onChange={(e) => setFilter({includeHistory: e.target.checked})}
                    >
                        Hiện cả bản đã thay (lịch sử)
                    </Checkbox>
                </div>
            </div>

            {loadError && <LoadError error={loadError} onRetry={fetchRows}/>}

            {!loadError && data.ambiguous.length > 0 && (
                <Notice level="warning">
                    <div>
                        <strong>{formatInt(data.ambiguous.length)} mã nhân viên đang giữ nhiều hơn một tài khoản</strong>
                        {' — '}
                        {data.ambiguous.map((entry) => `${entry.employeeCode} (${entry.actors.join(', ')})`).join(' · ')}
                    </div>
                    {/*
                      * Nói thẳng hệ quả, rồi chỉ ĐÚNG thao tác gỡ.
                      *
                      * ⚠️ Câu này đã SAI hai lần theo hai kiểu, nên đọc kỹ trước khi sửa lại:
                      *
                      *    1. Bản đầu chỉ viết "đóng hiệu lực tài khoản không còn dùng" — đúng mà
                      *       không làm được, vì màn này hiện cột Hiệu lực ở dạng CHỈ ĐỌC và service
                      *       không có endpoint sửa một dòng.
                      *    2. Bản sau dạy "nhập lại với Áp dụng từ = ngày chuyển tổ rồi tick Đóng
                      *       hiệu lực ở khối vắng mặt". Từ khi mốc đóng tách thành `closeAt` riêng,
                      *       câu đó dẫn người dùng tới ĐÚNG một kết quả sai: tài khoản cũ bị đóng
                      *       tại HÔM NAY chứ không phải ngày chuyển tổ, nên khoảng từ ngày chuyển
                      *       tổ tới hôm nay vẫn có hai tài khoản và vẫn bị bỏ qua — không lỗi nào
                      *       báo. Tệ hơn: ô "Áp dụng từ" chỉ hiện khi có dòng `added` thiếu ngày,
                      *       nên ở đúng ca này ô đó không xuất hiện để mà đặt. (Vòng review 2, R4.)
                      *
                      *    Đường đúng bây giờ là khai thẳng hai cột ngày TRONG FILE — xem file mẫu,
                      *    nó có sẵn ba dòng ví dụ ở đúng sheet người dùng gõ.
                      */}
                    <div className="VcHint">
                        Chấm công của những mã này bị BỎ QUA: một con số giờ công không chia được cho
                        hai tài khoản. Thường gặp khi <strong>chuyển tổ</strong> — mở tài khoản mới mà
                        chưa đóng tài khoản cũ.
                    </div>
                    <div className="VcHint">
                        Cách gỡ: trong file danh sách, điền <strong>Hiệu lực đến = ngày chuyển tổ</strong>
                        {' '}cho dòng tài khoản CŨ và <strong>Hiệu lực từ = ngày chuyển tổ</strong> cho
                        dòng tài khoản MỚI, rồi nhập lại. Hai cột đó là tuỳ chọn, file mẫu có sẵn ví dụ.
                        Cận trên LOẠI TRỪ: tài khoản cũ đúng tới hết ngày liền trước, ngày chuyển tổ
                        tính cho tài khoản mới.
                    </div>
                </Notice>
            )}

            {!loadError && (
                <Table
                    className="VcTable"
                    rowKey={(row) => `${row.employeeCode}|${row.actor}|${row.effectiveFrom || 'all'}`}
                    columns={columns}
                    dataSource={visibleRows}
                    loading={loading}
                    size="small"
                    pagination={{pageSize: 50, showSizeChanger: false}}
                    locale={{
                        emptyText: (
                            <div className="VcEmptyState">
                                {/*
                                  * Hai câu khác nhau cho hai tình huống khác nhau: chưa nhập bao giờ
                                  * thì chỉ sang nút Nhập, còn lọc không ra thì nói là do bộ lọc —
                                  * một câu dùng chung sẽ khiến người vừa gõ nhầm tưởng mất dữ liệu.
                                  */}
                                {data.rows.length > 0 ? (
                                    <div>Không tài khoản nào khớp bộ lọc.</div>
                                ) : (
                                    <>
                                        <div>Chưa có bản đồ tài khoản nào.</div>
                                        <div className="VcHint">
                                            Nhập từ file “Danh sách tài khoản nội bộ” (sheet 3D) để bắt đầu.
                                        </div>
                                    </>
                                )}
                            </div>
                        ),
                    }}
                />
            )}

            <Modal
                title="Nhập danh sách tài khoản"
                visible={importOpen}
                width={1320}
                onCancel={() => { resetImport(); setImportOpen(false) }}
                footer={[
                    <Button key="cancel" onClick={() => { resetImport(); setImportOpen(false) }}>Đóng</Button>,
                    /*
                      * Không có gì để ghi thì KHÔNG hiện nút. Một nút luôn hiện dù không làm gì là
                      * nút người dùng học cách bấm cho qua, rồi đến lúc nó thật sự làm gì đó thì họ
                      * vẫn bấm như cũ. `changeCount` gồm cả dòng chỉ sửa tên, nên nút vẫn hiện khi
                      * file chỉ đổi mỗi một cái tên gõ sai.
                      */
                    changeCount > 0 && (
                        <Button
                            key="ok"
                            type="primary"
                            disabled={!!blocker || previewing}
                            loading={saving}
                            onClick={onSubmit}
                        >
                            Nhập {formatInt(changeCount)} thay đổi
                        </Button>
                    ),
                ]}
            >
                <Upload
                    accept=".xlsx"
                    showUploadList={false}
                    beforeUpload={(picked) => {
                        onPick(picked)

                        return false
                    }}
                >
                    <Button loading={previewing}>{file ? `Đổi file (${file.name})` : 'Chọn file .xlsx'}</Button>
                </Upload>

                {/*
                  * File thật của bên 3D KHÔNG có hai cột ngày hiệu lực, nên không có file mẫu thì
                  * người dùng không có cách nào biết chúng tồn tại — mà đó là đường duy nhất để
                  * khai chuyển tổ ngay trong file.
                  */}
                <Button className="VcMl8" onClick={onDownloadTemplate} loading={downloadingTemplate}>
                    Tải file mẫu
                </Button>

                <div className="VcHint">
                    Chỉ đọc sheet <strong>3D</strong>. Cột mật khẩu không được đọc, không được lưu.
                    Hai cột <strong>Hiệu lực từ</strong> / <strong>Hiệu lực đến</strong> là tuỳ chọn —
                    dùng để khai chuyển tổ. Xem sheet <strong>Hướng dẫn</strong> trong file mẫu.
                </div>

                {previewError && <Alert type="error" showIcon className="VcMt8" message={previewError}/>}

                {preview && (
                    <>
                        <div className="VcImportStats VcMt8">
                            <div className="VcImportStat VcImportStat--ok">
                                <div className="VcImportStat__value">{formatInt(preview.added.length)}</div>
                                <div className="VcImportStat__label">Thêm mới</div>
                            </div>
                            <div className="VcImportStat">
                                <div className="VcImportStat__value">{formatInt(preview.changed.length)}</div>
                                <div className="VcImportStat__label">Đổi tài khoản</div>
                            </div>
                            <div className="VcImportStat">
                                <div className="VcImportStat__value">
                                    {formatInt(preview.unchangedCount + (preview.renamed || []).length)}
                                </div>
                                <div className="VcImportStat__label">
                                    Giữ tài khoản
                                    {(preview.renamed || []).length > 0 &&
                                        ` · ${formatInt(preview.renamed.length)} sửa tên/bộ phận`}
                                </div>
                            </div>
                            {/*
                              * ⚠️ Ô RIÊNG cho dòng sửa mốc hiệu lực, không gộp vào "Giữ tài khoản".
                              *    Service tách `retimed` ra vì nó là việc duy nhất trong lượt nhập
                              *    làm đổi CÁCH QUY ĐỔI của dữ liệu đã có — gộp nó vào một nhãn vô
                              *    hại là bày một việc nguy hiểm dưới nhãn vô hại. Bản đầu của F4
                              *    không bày gì cả: dòng rời `unchanged` mà không vào ô nào, nên ô
                              *    "Giữ tài khoản" còn TỤT đi so với trước lượt vá.
                              */}
                            {(preview.retimed || []).length > 0 && (
                                <div className="VcImportStat VcImportStat--warn">
                                    <div className="VcImportStat__value">
                                        {formatInt(preview.retimed.length)}
                                    </div>
                                    <div className="VcImportStat__label">Sửa mốc hiệu lực</div>
                                </div>
                            )}
                            <div className="VcImportStat">
                                <div className="VcImportStat__value">{formatInt(preview.missing.length)}</div>
                                <div className="VcImportStat__label">Vắng mặt trong file</div>
                            </div>
                        </div>

                        {preview.firstImport && (
                            <Alert
                                type="info"
                                showIcon
                                className="VcMt8"
                                message="Lượt nhập đầu tiên — không cần khai ngày hiệu lực"
                                description={
                                    'Chưa có bản đồ nào trước đó nên không có gì để phân định: bản này có hiệu lực ' +
                                    'với mọi mốc thời gian, kể cả file chấm công của các tháng đã qua.'
                                }
                            />
                        )}

                        <div className="VcSectionTitle VcMt8">Các dòng sẽ ghi</div>

                        <div className="VcPreviewTools">
                            <Radio.Group
                                value={previewMode}
                                size="small"
                                onChange={(e) => setPreviewMode(e.target.value)}
                            >
                                <Radio.Button value="all">
                                    Tất cả (
                                    {formatInt(
                                        preview.added.length +
                                            preview.changed.length +
                                            (preview.retimed || []).length
                                    )}
                                    )
                                </Radio.Button>
                                <Radio.Button value="added">
                                    Thêm mới ({formatInt(preview.added.length)})
                                </Radio.Button>
                                <Radio.Button value="changed">
                                    Đổi tài khoản ({formatInt(preview.changed.length)})
                                </Radio.Button>
                                {(preview.retimed || []).length > 0 && (
                                    <Radio.Button value="retimed">
                                        Sửa mốc ({formatInt(preview.retimed.length)})
                                    </Radio.Button>
                                )}
                                {/*
                                  * Bộ lọc này tồn tại vì trong 62 dòng thì hai dòng của cùng một mã
                                  * rơi vào hai trang khác nhau và gần như không thể nhìn thấy cùng
                                  * lúc — mà việc người dùng cần làm chính là SO hai dòng đó để chọn
                                  * giữ tài khoản nào. Ẩn hẳn khi không có dòng nào: một nút luôn
                                  * hiện "(0)" là nút người dùng học cách bỏ qua.
                                  */}
                                {ambiguousRowCount > 0 && (
                                    <Radio.Button value="ambiguous">
                                        Sẽ bị bỏ qua ({formatInt(ambiguousRowCount)})
                                    </Radio.Button>
                                )}
                            </Radio.Group>

                            <Select
                                size="small"
                                style={{minWidth: 150}}
                                value={previewDepartment || undefined}
                                placeholder="Mọi bộ phận"
                                allowClear
                                onChange={(value) => setPreviewDepartment(value || '')}
                            >
                                {previewDepartments.map((name) => (
                                    <Select.Option key={name} value={name}>{name}</Select.Option>
                                ))}
                            </Select>

                            {/*
                              * Tìm trên CẢ bốn cột, không bắt chọn cột trước: người dùng gõ `00607`
                              * hay `tunt` hay `Tú` đều là đang tìm đúng một người. Bỏ dấu khi so
                              * khớp nên gõ `tu` vẫn ra "Nguyễn Trọng Tú".
                              */}
                            <Input.Search
                                size="small"
                                allowClear
                                style={{maxWidth: 280}}
                                value={previewSearch}
                                placeholder="Tìm mã NV, họ tên, tài khoản…"
                                onChange={(e) => setPreviewSearch(e.target.value)}
                            />

                            {(previewSearch || previewDepartment) && (
                                <span className="VcMuted">
                                    {formatInt(previewRows.length)}/{formatInt(previewScope.length)} dòng
                                </span>
                            )}
                        </div>

                        <Table
                            className="VcTable VcMt8"
                            rowKey={(row) => changeKey(row.employeeCode, row.actor)}
                            columns={previewColumns}
                            dataSource={previewRows}
                            size="small"
                            pagination={{pageSize: 20, showSizeChanger: false, size: 'small'}}
                            /*
                             * KHÔNG `scroll.y`: bảng cuộn trong khi modal cũng cuộn là hai thanh
                             * cuộn lồng nhau — bánh xe chuột dừng ở thanh trong và người dùng không
                             * ra được phần bên dưới bảng nếu con trỏ còn nằm trên nó.
                             */
                            locale={{
                                emptyText: (() => {
                                    // Ba câu khác nhau cho ba tình huống khác nhau. Một câu dùng
                                    // chung thì người vừa gõ nhầm từ khoá sẽ tưởng file rỗng.
                                    if (previewSearch || previewDepartment) return 'Không dòng nào khớp bộ lọc.'

                                    return preview.unchangedCount > 0
                                        ? 'File trùng khít bản đang dùng — lượt nhập này chỉ cập nhật tên và bộ phận.'
                                        : 'Không có dòng nào trong mục này.'
                                })(),
                            }}
                        />

                        {changeCount === 0 && (
                            <Alert
                                type="success"
                                showIcon
                                className="VcMt8"
                                message="File trùng khít bản đang dùng — không có gì để ghi"
                                description={
                                    'Mọi dòng đều khớp cả tài khoản lẫn tên và bộ phận. Đóng cửa sổ này; ' +
                                    'lượt nhập sẽ không thay đổi gì.'
                                }
                            />
                        )}

                        {preview.changed.length > 0 && (
                            <>
                                <div className="VcSectionTitle VcMt8">Đổi tài khoản — chọn mốc áp dụng cho từng dòng</div>
                                <div className="VcHint">
                                    Mốc này quyết định file chấm công của kỳ nào quy về tài khoản nào.
                                    Mặc định là hôm nay; sửa lại nếu biết người đó chuyển từ ngày khác.
                                </div>
                                <Table
                                    className="VcTable VcMt8"
                                    rowKey={(row) => changeKey(row.employeeCode, row.actor)}
                                    columns={changeColumns}
                                    dataSource={preview.changed}
                                    size="small"
                                    pagination={false}
                                />
                            </>
                        )}

                        {ambiguous.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(ambiguous.length)} mã sẽ giữ nhiều hơn một tài khoản`}
                                description={
                                    <>
                                        <div>
                                            {ambiguous
                                                .map((entry) => `${entry.employeeCode} (${entry.actors.join(', ')})`)
                                                .join(' · ')}
                                        </div>
                                        {/*
                                          * ⚠️ Cùng câu, cùng lỗi, cùng bản vá với Notice cố định ở
                                          *    trên — xem ghi chú dài ở đó. Hai chỗ nói về MỘT thao
                                          *    tác thì phải sửa cùng lúc; bản trước chỉ sửa một chỗ.
                                          */}
                                        <div className="VcHint">
                                            Chấm công của những mã này sẽ bị bỏ qua: một con số giờ
                                            công không chia được cho hai tài khoản. Nếu là{' '}
                                            <strong>chuyển tổ</strong>, hãy điền{' '}
                                            <strong>Hiệu lực đến</strong> cho dòng tài khoản cũ và{' '}
                                            <strong>Hiệu lực từ</strong> cho dòng tài khoản mới, ngay
                                            trong file, rồi nhập lại.
                                        </div>
                                    </>
                                }
                            />
                        )}

                        {toClose.length > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(toClose.length)} tài khoản đang dùng sẽ bị ĐÓNG hiệu lực`}
                                description={
                                    <>
                                        <div>
                                            {toClose
                                                .slice(0, 20)
                                                .map((e) => `${e.employeeCode}: ${e.actor} → ${e.newActor}`)
                                                .join(' · ')}
                                        </div>
                                        {/*
                                          * ⚠️ Trùng mã mà khác tài khoản có thể là chuyển tổ (đúng
                                          * là phải đóng tài khoản cũ), cũng có thể là gõ nhầm tên
                                          * đăng nhập. Dữ liệu không phân biệt được, mà nhập lại
                                          * file KHÔNG hoàn tác được — dòng cũ đã hết hiệu lực.
                                          */}
                                        <Checkbox
                                            className="VcMt8"
                                            checked={confirmClose}
                                            onChange={(e) => setConfirmClose(e.target.checked)}
                                        >
                                            Đúng, đóng hiệu lực những tài khoản cũ này
                                        </Checkbox>
                                        <div className="VcHint">
                                            Sản lượng đã ghi dưới tài khoản cũ vẫn còn, nhưng từ ngày
                                            áp dụng trở đi mã nhân viên không còn trỏ về nó nữa.
                                            Nếu chỉ là gõ nhầm tên đăng nhập thì sửa file rồi chọn lại.
                                        </div>
                                    </>
                                }
                            />
                        )}

                        {closingByFile.length > 0 && (
                            <Alert
                                type="info"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(closingByFile.length)} tài khoản được đóng theo cột "Hiệu lực đến" của file`}
                                description={
                                    <>
                                        <div>
                                            {closingByFile
                                                .slice(0, 20)
                                                .map((e) => `${e.employeeCode} (${e.actor}) → hết hiệu lực ${e.effectiveTo}`)
                                                .join(' · ')}
                                        </div>
                                        {/*
                                          * Cận trên LOẠI TRỪ — nói ra vì đây là chỗ người dùng dễ
                                          * lệch đúng một ngày mà không ai phát hiện.
                                          */}
                                        <div className="VcHint">
                                            Ngày này KHÔNG còn tính cho tài khoản đó: nó có hiệu lực
                                            đến hết ngày liền trước. Khai đúng ngày chuyển tổ thì
                                            không ngày nào bị tính hai lần, cũng không ngày nào rơi mất.
                                        </div>
                                    </>
                                }
                            />
                        )}

                        {withoutFrom.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(withoutFrom.length)} dòng không khai "Hiệu lực từ"`}
                                description={
                                    <>
                                        <div className="VcInlineField">
                                            <span>Những dòng này sẽ lấy mốc</span>
                                            {/*
                                              * Ô CHỌN được, không phải chữ chết: mốc hiệu lực quyết
                                              * định chấm công từ kỳ nào quy được về tài khoản đó, mà
                                              * chỉ người dùng mới biết người ta bắt đầu làm từ bao
                                              * giờ. Đổi ở đây là cột "Áp dụng từ" của bảng bên dưới
                                              * tính lại ngay — hai chỗ đọc cùng một giá trị.
                                              */}
                                            <DatePicker
                                                size="small"
                                                format="DD/MM/YYYY"
                                                value={appliedFromUsed ? moment(appliedFromUsed, DAY) : null}
                                                placeholder={preview.defaultEffectiveFrom || 'Chọn ngày'}
                                                onChange={(picked) => setAppliedFrom(picked ? picked.format(DAY) : '')}
                                            />
                                            {appliedFrom && (
                                                <Button
                                                    size="small"
                                                    type="link"
                                                    onClick={() => setAppliedFrom('')}
                                                >
                                                    Về mốc mặc định ({preview.defaultEffectiveFrom || DASH})
                                                </Button>
                                            )}
                                        </div>
                                        {/*
                                          * ⚠️ Mốc hiệu lực quyết định chấm công từ kỳ nào quy được
                                          * về tài khoản đó. Chọn hộ rồi im lặng là âm thầm quyết
                                          * định hộ người dùng một chuyện họ kiểm được.
                                          */}
                                        <div className="VcHint">
                                            Chấm công của kỳ TRƯỚC mốc này sẽ không quy được về tài
                                            khoản nào. Mốc này áp cho{' '}
                                            <strong>mọi dòng không tự khai &ldquo;Hiệu lực từ&rdquo;</strong>,
                                            gồm cả dòng đổi tài khoản — xem cột &ldquo;Áp dụng từ&rdquo;
                                            ở bảng bên dưới để biết từng dòng sẽ dùng ngày nào. Nó{' '}
                                            <strong>không</strong> ảnh hưởng tới mốc đóng tài khoản
                                            vắng mặt. Muốn mỗi người một ngày thì điền cột
                                            &ldquo;Hiệu lực từ&rdquo; trong file (xem file mẫu).
                                        </div>
                                    </>
                                }
                            />
                        )}

                        {preview.missing.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(preview.missing.length)} tài khoản đang dùng nhưng VẮNG MẶT trong file`}
                                description={
                                    <>
                                        <div>
                                            {preview.missing
                                                .slice(0, 20)
                                                .map((entry) => `${entry.employeeCode} (${entry.actor})`)
                                                .join(' · ')}
                                        </div>
                                        {/*
                                          * Mặc định KHÔNG đóng, và nói rõ vì sao. Vắng mặt có thể là
                                          * người đã nghỉ, cũng có thể là file xuất thiếu — không
                                          * phân biệt được từ dữ liệu, nên đây phải là thao tác có
                                          * chủ đích chứ không phải hệ quả của việc nhập.
                                          */}
                                        <Checkbox
                                            className="VcMt8"
                                            checked={closeMissing}
                                            onChange={(e) => setCloseMissing(e.target.checked)}
                                        >
                                            Đóng hiệu lực những tài khoản này
                                        </Checkbox>
                                        <div className="VcHint">
                                            Chỉ tick khi chắc chắn họ đã nghỉ. File xuất thiếu một phần
                                            mà tick vào là khoá luôn những người vẫn đang làm.
                                        </div>
                                        {/*
                                          * ⚠️ Ô chọn ngày PHẢI có ở đây, không chỉ ở khối "thiếu
                                          * Hiệu lực từ". File 3D THẬT không có cột ngày nào, nên ca
                                          * chuyển tổ qua nó cho ra `changed` + `missing` và KHÔNG có
                                          * dòng `added` nào — khối kia không hiện, ô chọn ngày biến
                                          * mất, trong khi hai dòng hướng dẫn trên màn lại bảo người
                                          * dùng "nhập lại với Áp dụng từ = ngày chuyển tổ". Thiếu nó
                                          * thì `closeMissing` luôn đóng tại HÔM NAY và hướng dẫn in
                                          * ra màn hình không làm theo được. (Vòng review 2.)
                                          */}
                                        <div className="VcInlineField VcMt8">
                                            <span>Đóng tại ngày</span>
                                            <DatePicker
                                                size="small"
                                                format="DD/MM/YYYY"
                                                value={closeAt ? moment(closeAt, DAY) : null}
                                                placeholder={preview.defaultAppliedFrom || 'Hôm nay'}
                                                onChange={(picked) => setCloseAt(picked ? picked.format(DAY) : '')}
                                            />
                                            {closeAt && (
                                                <Button size="small" type="link" onClick={() => setCloseAt('')}>
                                                    Về hôm nay
                                                </Button>
                                            )}
                                        </div>
                                    </>
                                }
                            />
                        )}

                        {(preview.issues || []).length > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(preview.issues.length)} dòng có vấn đề — những dòng này bị bỏ`}
                                description={
                                    <ul className="VcSkipList">
                                        {preview.issues.slice(0, 10).map((issue) => (
                                            <li key={`${issue.row}-${issue.code}`}>
                                                Dòng {issue.row}: {issue.message}
                                            </li>
                                        ))}
                                    </ul>
                                }
                            />
                        )}

                        {blocker && <Alert type="warning" showIcon className="VcMt8" message={blocker}/>}
                    </>
                )}
            </Modal>
        </div>
    )
}

export default WorkerAccountsPage
